import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, Save, X, AlertTriangle, 
  CheckCircle, Package, Tag, DollarSign, Image, 
  Edit, Eye, Copy, Upload
} from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { supabase } from '../../lib/supabase';

const SellerVariantsPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [product, setProduct] = useState<any>(null);
  const [variants, setVariants] = useState<any[]>([]);
  const [attributes, setAttributes] = useState<any[]>([]);
  const [attributeValues, setAttributeValues] = useState<any[]>([]);
  
  // New variant form
  const [newVariant, setNewVariant] = useState({
    sku: '',
    price: '',
    original_price: '',
    stock_quantity: '0',
    is_default: false,
    image_url: '',
    attributes: {} as Record<string, string>
  });
  
  // Check if user is logged in and has access to this product
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        navigate('/signin?redirect=seller-central');
        return;
      }
      
      if (!productId) {
        navigate('/seller-central/products');
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch product
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select(`
            *,
            suppliers!inner(
              id,
              user_id
            )
          `)
          .eq('id', productId)
          .single();
        
        if (productError) throw productError;
        
        // Check if user is the supplier
        if (productData.suppliers.user_id !== user.id) {
          throw new Error('You do not have permission to manage this product');
        }
        
        setProduct(productData);
        
        // Fetch product variants
        const { data: variantsData, error: variantsError } = await supabase
          .from('product_variants')
          .select('*')
          .eq('product_id', productId)
          .order('is_default', { ascending: false });
        
        if (variantsError) throw variantsError;
        
        setVariants(variantsData || []);
        
        // Fetch product attributes
        const { data: attributesData, error: attributesError } = await supabase
          .from('product_attributes')
          .select('*')
          .order('name');
        
        if (attributesError) throw attributesError;
        
        setAttributes(attributesData || []);
        
        // Fetch attribute values
        const { data: valuesData, error: valuesError } = await supabase
          .from('product_attribute_values')
          .select('*')
          .order('value');
        
        if (valuesError) throw valuesError;
        
        setAttributeValues(valuesData || []);
        
        // Initialize new variant with default values
        setNewVariant(prev => ({
          ...prev,
          price: productData.price.toString(),
          original_price: productData.original_price ? productData.original_price.toString() : '',
          is_default: variantsData.length === 0
        }));
        
      } catch (err) {
        console.error('Error checking access:', err);
        setError(err instanceof Error ? err.message : 'Failed to load product data');
      } finally {
        setLoading(false);
      }
    };
    
    checkAccess();
  }, [user, productId, navigate]);
  
  // Handle input change for new variant
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setNewVariant(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'price' || name === 'original_price' || name === 'stock_quantity') {
      // Only allow numbers and decimal point for price fields
      if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
        setNewVariant(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setNewVariant(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Handle attribute change for new variant
  const handleAttributeChange = (attributeName: string, value: string) => {
    setNewVariant(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [attributeName]: value
      }
    }));
  };
  
  // Upload image for new variant
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      // Show a temporary preview while uploading
      const objectUrl = URL.createObjectURL(file);
      setNewVariant(prev => ({
        ...prev,
        image_url: objectUrl
      }));
      
      // Upload the file to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${productId}/variants/${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) throw error;
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(data.path);
      
      // Update the form data with the public URL
      setNewVariant(prev => ({
        ...prev,
        image_url: publicUrl
      }));
      
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
      
      // Reset image URL
      setNewVariant(prev => ({
        ...prev,
        image_url: ''
      }));
    }
  };
  
  // Add new variant
  const handleAddVariant = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !productId) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Validate required fields
      if (!newVariant.price) {
        setError('Price is required');
        setSaving(false);
        return;
      }
      
      // Check if attributes are selected
      const attributeKeys = Object.keys(newVariant.attributes);
      if (attributeKeys.length === 0) {
        setError('Please select at least one attribute');
        setSaving(false);
        return;
      }
      
      // Check if this variant combination already exists
      const existingVariant = variants.find(variant => {
        const variantAttributes = variant.attributes || {};
        return attributeKeys.every(key => variantAttributes[key] === newVariant.attributes[key]);
      });
      
      if (existingVariant) {
        setError('A variant with these attributes already exists');
        setSaving(false);
        return;
      }
      
      // If this is the default variant, update other variants
      if (newVariant.is_default) {
        // Update all other variants to not be default
        for (const variant of variants) {
          if (variant.is_default) {
            await supabase
              .from('product_variants')
              .update({ is_default: false })
              .eq('id', variant.id);
          }
        }
      }
      
      // Create new variant
      const { data, error } = await supabase
        .from('product_variants')
        .insert({
          product_id: productId,
          sku: newVariant.sku || null,
          price: parseFloat(newVariant.price),
          original_price: newVariant.original_price ? parseFloat(newVariant.original_price) : null,
          stock_quantity: parseInt(newVariant.stock_quantity) || 0,
          is_default: newVariant.is_default,
          image_url: newVariant.image_url || null,
          attributes: newVariant.attributes
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Add new variant to list
      setVariants([...variants, data]);
      
      // Reset form
      setNewVariant({
        sku: '',
        price: product.price.toString(),
        original_price: product.original_price ? product.original_price.toString() : '',
        stock_quantity: '0',
        is_default: false,
        image_url: '',
        attributes: {}
      });
      
      setSuccess('Variant added successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error adding variant:', err);
      setError(err instanceof Error ? err.message : 'Failed to add variant');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete variant
  const handleDeleteVariant = async (variantId: string) => {
    if (!confirm('Are you sure you want to delete this variant?')) return;
    
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('product_variants')
        .delete()
        .eq('id', variantId);
      
      if (error) throw error;
      
      // Remove variant from list
      setVariants(variants.filter(v => v.id !== variantId));
      
      setSuccess('Variant deleted successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error deleting variant:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete variant');
    } finally {
      setLoading(false);
    }
  };
  
  // Set variant as default
  const handleSetDefault = async (variantId: string) => {
    try {
      setLoading(true);
      
      // Update all variants
      for (const variant of variants) {
        await supabase
          .from('product_variants')
          .update({ is_default: variant.id === variantId })
          .eq('id', variant.id);
      }
      
      // Update local state
      setVariants(variants.map(v => ({
        ...v,
        is_default: v.id === variantId
      })));
      
      setSuccess('Default variant updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error setting default variant:', err);
      setError(err instanceof Error ? err.message : 'Failed to update default variant');
    } finally {
      setLoading(false);
    }
  };
  
  // Get attribute display name
  const getAttributeDisplayName = (attributeName: string) => {
    const attribute = attributes.find(a => a.name === attributeName);
    return attribute ? attribute.display_name : attributeName;
  };
  
  // Get attribute value display name
  const getAttributeValueDisplayName = (attributeName: string, value: string) => {
    const attribute = attributes.find(a => a.name === attributeName);
    if (!attribute) return value;
    
    const attributeValue = attributeValues.find(v => v.attribute_id === attribute.id && v.value === value);
    return attributeValue ? attributeValue.display_value : value;
  };
  
  // Get attribute values for a specific attribute
  const getAttributeValues = (attributeName: string) => {
    const attribute = attributes.find(a => a.name === attributeName);
    if (!attribute) return [];
    
    return attributeValues.filter(v => v.attribute_id === attribute.id);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Error</h2>
          <p className="text-gray-600 mb-6 text-center">{error}</p>
          <div className="flex justify-center">
            <Link 
              to="/seller-central/products" 
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            >
              Back to Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/seller-central/products" className="mr-4 text-gray-500 hover:text-gray-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Product Variants</h1>
                <p className="text-gray-600">Manage variants for {product?.name}</p>
              </div>
            </div>
            <Link 
              to={`/product/${productId}`} 
              target="_blank" 
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center space-x-2"
            >
              <Eye className="h-4 w-4" />
              <span>View Product</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span>{success}</span>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Existing Variants */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Existing Variants ({variants.length})</h2>
              
              {variants.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No Variants Yet</h3>
                  <p className="text-gray-600 mb-4">
                    Add your first product variant using the form on the right.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {variants.map((variant) => (
                    <div 
                      key={variant.id} 
                      className={`border rounded-lg overflow-hidden ${
                        variant.is_default ? 'border-green-300 bg-green-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="p-4">
                        <div className="flex items-start">
                          {/* Variant Image */}
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 mr-4">
                            {variant.image_url ? (
                              <img 
                                src={variant.image_url} 
                                alt="Variant" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="h-8 w-8 text-gray-400" />
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <h3 className="font-medium text-gray-900">
                                  {variant.sku ? `SKU: ${variant.sku}` : 'Variant'}
                                </h3>
                                {variant.is_default && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Default
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleDeleteVariant(variant.id)}
                                  className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                                <Link
                                  to={`/seller-central/products/${productId}/variants/${variant.id}/edit`}
                                  className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded"
                                >
                                  <Edit className="h-4 w-4" />
                                </Link>
                                {!variant.is_default && (
                                  <button
                                    onClick={() => handleSetDefault(variant.id)}
                                    className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                                    title="Set as default"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div>
                                <span className="text-sm text-gray-500">Price:</span>
                                <span className="ml-2 text-sm font-medium text-gray-900">${variant.price}</span>
                              </div>
                              <div>
                                <span className="text-sm text-gray-500">Stock:</span>
                                <span className="ml-2 text-sm font-medium text-gray-900">{variant.stock_quantity}</span>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              {variant.attributes && Object.entries(variant.attributes).map(([key, value]) => (
                                <span key={key} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {getAttributeDisplayName(key)}: {getAttributeValueDisplayName(key, value as string)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add New Variant */}
          <div>
            <form onSubmit={handleAddVariant} className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Add New Variant</h2>
              
              <div className="space-y-6 mb-6">
                {/* Variant Attributes */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Attributes</h3>
                  <div className="space-y-4">
                    {attributes.map(attribute => (
                      <div key={attribute.id}>
                        <label className="block text-sm text-gray-600 mb-1">
                          {attribute.display_name} {attribute.is_required && '*'}
                        </label>
                        <select
                          value={newVariant.attributes[attribute.name] || ''}
                          onChange={(e) => handleAttributeChange(attribute.name, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          required={attribute.is_required}
                        >
                          <option value="">Select {attribute.display_name}</option>
                          {getAttributeValues(attribute.name).map(value => (
                            <option key={value.id} value={value.value}>
                              {value.display_value} {value.additional_cost > 0 && `(+$${value.additional_cost})`}
                              {value.additional_cost < 0 && `(-$${Math.abs(value.additional_cost)})`}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* SKU */}
                <div>
                  <label htmlFor="sku" className="block text-sm font-medium text-gray-700 mb-2">
                    SKU
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="sku"
                      name="sku"
                      type="text"
                      value={newVariant.sku}
                      onChange={handleInputChange}
                      className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="e.g., PROD-VAR-001"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Optional. Leave blank to auto-generate.
                  </p>
                </div>
                
                {/* Price */}
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                    Price *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="price"
                      name="price"
                      type="text"
                      required
                      value={newVariant.price}
                      onChange={handleInputChange}
                      className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                
                {/* Original Price */}
                <div>
                  <label htmlFor="original_price" className="block text-sm font-medium text-gray-700 mb-2">
                    Original Price
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="original_price"
                      name="original_price"
                      type="text"
                      value={newVariant.original_price}
                      onChange={handleInputChange}
                      className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Optional. For showing discounted prices.
                  </p>
                </div>
                
                {/* Stock Quantity */}
                <div>
                  <label htmlFor="stock_quantity" className="block text-sm font-medium text-gray-700 mb-2">
                    Stock Quantity
                  </label>
                  <input
                    id="stock_quantity"
                    name="stock_quantity"
                    type="number"
                    min="0"
                    value={newVariant.stock_quantity}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
                
                {/* Is Default */}
                <div className="flex items-center">
                  <input
                    id="is_default"
                    name="is_default"
                    type="checkbox"
                    checked={newVariant.is_default}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_default" className="ml-2 block text-sm text-gray-900">
                    Set as default variant
                  </label>
                </div>
                
                {/* Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Variant Image
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    {newVariant.image_url ? (
                      <div className="relative">
                        <img 
                          src={newVariant.image_url} 
                          alt="Variant preview" 
                          className="w-full h-48 object-contain rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => setNewVariant(prev => ({ ...prev, image_url: '' }))}
                          className="absolute top-2 right-2 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <input
                          type="file"
                          id="image"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="image"
                          className="cursor-pointer inline-flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <Upload className="h-4 w-4" />
                          <span>Upload Image</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-2">
                          Optional. If not provided, the main product image will be used.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    <span>Add Variant</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerVariantsPage;